import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { projectsInfiniteQuery } from '@/entities/project';
import { SEARCH_DEBOUNCE_MS } from '@/shared/constants/search.constants';
import { useDebounce } from '@/shared/hooks/useDebounce/useDebounce';
import { FilterChipPopover, type FilterChipOption } from '@/shared/ui/FilterChipPopover';
import { useTranslation } from 'react-i18next';

interface Props {
  workspaceId: string | null | undefined;
  selectedProjectId: string | null;
  onProjectSelected: (projectId: string) => void;
  searchInput: string;
  onSearchInputChange: (next: string) => void;
}

/**
 * Inline project selector for the single-screen picker control row. Renders the
 * shared `FilterChipPopover` (single-select, `Project: <title>` trigger) so it
 * matches the Sort + Type chips beside it and inherits their keyboard nav.
 *
 * The project list is server-searched + paginated, so the chip runs in async
 * mode: the search input is controlled here, `projectsInfiniteQuery` filters and
 * paginates server-side, and the chip's bottom sentinel drives `fetchNextPage`.
 * `hideReset` suppresses the "All"/clear affordances — a project is always
 * selected. Stays in `features/` and imports only `@/entities/*` + `@/shared/*`
 * (FSD-clean; `features/` must not reach into `widgets/`).
 */
export function ProjectSelectInline(props: Props) {
  const { t } = useTranslation('projects');

  const debouncedSearch = useDebounce(props.searchInput, SEARCH_DEBOUNCE_MS);
  const trimmedSearch = debouncedSearch.trim();

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery(
    projectsInfiniteQuery(props.workspaceId, {
      sortBy: 'title',
      sortOrder: 'ASC',
      search: trimmedSearch || undefined,
    }),
  );

  const projects = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);

  const options = useMemo<ReadonlyArray<FilterChipOption<string>>>(
    () =>
      // Value is the platform project id: the selection drives project-scoped
      // briefs/artifacts queries, which 403 on the internal id. Drafts (no
      // platform project) are dropped — they have no context rows to show.
      projects.flatMap((project) =>
        project.platformProjectId
          ? [
              {
                value: project.platformProjectId,
                // Drafts can have a null title; show a stable placeholder so rows stay readable.
                label: project.title ?? t('addProjectContext.projectSelectInline.untitledProject'),
              },
            ]
          : [],
      ),
    [projects, t],
  );

  const handleChange = (next: string | null) => {
    // `hideReset` removes every clear affordance, so `next` is always a project.
    if (next !== null) {
      props.onProjectSelected(next);
    }
  };

  return (
    <FilterChipPopover<string>
      mode="single"
      label={t('addProjectContext.projectSelectInline.project')}
      triggerLabelStyle="label"
      triggerAriaLabel={t('addProjectContext.projectSelectInline.selectProject')}
      searchPlaceholder={t('addProjectContext.projectSelectInline.searchProjects')}
      // With `hideReset` the reset row is gone; this label is reused only as the
      // trigger fallback when the selected project isn't in the loaded list.
      resetOptionLabel={t('addProjectContext.projectSelectInline.untitledProject')}
      hideReset
      options={options}
      value={props.selectedProjectId}
      onChange={handleChange}
      asyncOptions={{
        search: props.searchInput,
        onSearchChange: props.onSearchInputChange,
        isLoading: isLoading || isFetchingNextPage,
        hasNextPage,
        onLoadMore: () => {
          void fetchNextPage();
        },
      }}
    />
  );
}
